import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Search, Tag } from 'lucide-react';
import toast from 'react-hot-toast';
import { brandService } from '../../services';

const BrandMaster = () => {
  const [brands, setBrands] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  // Load brands from API
  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setLoading(true);
      const response = await brandService.getAll({ search: searchTerm });
      setBrands(response.data || []);
    } catch (error) {
      console.error('Error loading brands:', error);
      toast.error('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  // Reload brands when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadBrands();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const openModal = (brand = null) => {
    setEditingBrand(brand);
    setShowModal(true);
    if (brand) {
      reset({
        brands: brand.brands,
        description: brand.description,
        status: brand.status
      });
    } else {
      reset({ brands: '', description: '', status: 1 });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBrand(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (editingBrand) {
        // Update brand
        await brandService.update(editingBrand.id, data);
        toast.success('Brand updated successfully!');
      } else {
        // Create new brand
        await brandService.create(data);
        toast.success('Brand created successfully!');
      }
      closeModal();
      loadBrands(); // Reload the list
    } catch (error) {
      console.error('Error saving brand:', error);
      toast.error('Failed to save brand');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (brand) => {
    try {
      setLoading(true);
      await brandService.toggleStatus(brand.id);
      toast.success('Brand status updated!');
      loadBrands(); // Reload the list
    } catch (error) {
      console.error('Error updating brand status:', error);
      toast.error('Failed to update brand status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
            Brand Master
          </h1>
          <button
            onClick={() => openModal()}
            className="btn btn-primary"
          >
            <Plus size={18} />
            Add Brand
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }}
          />
          <input
            type="text"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-box"
            style={{ paddingLeft: '40px' }}
          />
        </div>

        {/* Brands Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Brand Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Updated Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((brand) => (
                <tr key={brand.id}>
                  <td>{brand.id}</td>
                  <td style={{ fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Tag size={16} color="#6b7280" />
                      {brand.brands}
                    </div>
                  </td>
                  <td>
                    <div style={{
                      maxWidth: '300px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#6b7280'
                    }}>
                      {brand.description || '-'}
                    </div>
                  </td>
                  <td>
                    <button
                      onClick={() => toggleStatus(brand)}
                      className={`status-badge ${brand.status === 1 ? 'status-active' : 'status-inactive'}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                      disabled={loading}
                    >
                      {brand.status === 1 ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>{new Date(brand.created_at).toLocaleDateString()}</td>
                  <td>{new Date(brand.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openModal(brand)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                      >
                        <Edit size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            Loading brands...
          </div>
        )}

        {!loading && brands.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            No brands found.
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingBrand ? 'Edit Brand' : 'Add New Brand'}
              </h2>
              <button onClick={closeModal} className="close-btn">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Brand Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter brand name (e.g., Nike, Adidas)"
                  {...register('brands', {
                    required: 'Brand name is required',
                    minLength: {
                      value: 2,
                      message: 'Brand name must be at least 2 characters'
                    }
                  })}
                />
                {errors.brands && (
                  <div className="error-message">{errors.brands.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  placeholder="Enter brand description (optional)"
                  rows={4}
                  {...register('description')}
                />
                {errors.description && (
                  <div className="error-message">{errors.description.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  {...register('status', { required: 'Status is required' })}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
                {errors.status && (
                  <div className="error-message">{errors.status.message}</div>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingBrand ? 'Update Brand' : 'Create Brand'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandMaster;

